import { useMemo } from 'react'
import { useCityStore } from '@/store/cityStore'
import { useFilterOptions } from '@/hooks/api/useFilterOptions'
import { PATHS } from '@/routes/paths'
import { areaFilterValue, topAreasByCount } from '@/lib/areaFilterUtils'
import {
  buildPropertyHref,
  splitCategories,
  titleCase,
  type NavLink,
} from '@/lib/propertyNavLinks'

export interface FooterPropertyNav {
  propertyLinks: NavLink[]
  residentialLinks: NavLink[]
  commercialLandLinks: NavLink[]
}

/**
 * Footer property + popular-search links scoped to the header-selected city,
 * using the same live filter-options data as the mega menu.
 */
export function useFooterPropertyLinks(): FooterPropertyNav {
  const cityName = useCityStore((s) => s.cityName)
  const { options } = useFilterOptions(cityName)

  return useMemo<FooterPropertyNav>(() => {
    const city = cityName?.trim() || 'Ahmedabad'
    const cityLower = city.toLowerCase()

    const propertyLinks: NavLink[] = [
      { label: 'Buy Property', href: buildPropertyHref('buy', { city: cityLower }) },
      { label: 'Rent Property', href: buildPropertyHref('rent', { city: cityLower }) },
      { label: 'Commercial', href: buildPropertyHref('commercial', { city: cityLower }) },
      {
        label: 'New Projects',
        href: buildPropertyHref('buy', {
          city: cityLower,
          category: 'New Project',
          categorySlug: 'new-project',
        }),
      },
      { label: 'Post Property Free', href: PATHS.addProperty },
    ]

    const { residential, commercial, land } = splitCategories(options.categories ?? [])
    const areas = topAreasByCount(options.areas ?? [], city, 4)

    const residentialLinks: NavLink[] = [
      { label: `2 BHK in ${city}`, href: buildPropertyHref('buy', { city: cityLower, bhk: '2 BHK' }) },
      { label: `3 BHK in ${city}`, href: buildPropertyHref('buy', { city: cityLower, bhk: '3 BHK' }) },
      ...areas.slice(0, 2).map((area) => ({
        label: `Properties in ${titleCase(area.name)}`,
        href: buildPropertyHref('', {
          city: cityLower,
          area: areaFilterValue(area),
        }),
      })),
      ...residential.slice(0, 2).map((cat) => ({
        label: `${cat.name} in ${city}`,
        href: buildPropertyHref('buy', {
          city: cityLower,
          category: cat.name,
          categorySlug: cat.slug || undefined,
        }),
      })),
    ]

    const commercialLandLinks: NavLink[] = [
      ...commercial.slice(0, 3).map((cat) => ({
        label: `${cat.name} in ${city}`,
        href: buildPropertyHref('', {
          city: cityLower,
          category: cat.name,
          categorySlug: cat.slug || undefined,
        }),
      })),
      ...land.slice(0, 3).map((cat) => ({
        label: `${cat.name} in ${city}`,
        href: buildPropertyHref('buy', {
          city: cityLower,
          category: cat.name,
          categorySlug: cat.slug || undefined,
        }),
      })),
    ]

    // De-dupe by href while preserving order.
    const unique = (links: NavLink[]) => {
      const seen = new Set<string>()
      return links.filter((link) => {
        if (seen.has(link.href)) return false
        seen.add(link.href)
        return true
      })
    }

    return {
      propertyLinks,
      residentialLinks: unique(residentialLinks).slice(0, 6),
      commercialLandLinks: unique(commercialLandLinks).slice(0, 6),
    }
  }, [cityName, options])
}
